import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, XCircle, Upload, ClipboardCheck, FileCheck } from "lucide-react";
import { api } from "@/lib/apiClient";
import { Loader, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Assessment() {
  const { phaseId } = useParams();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [evidence, setEvidence] = useState(null);
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [tab, setTab] = useState(params.get("tab") === "evidence" ? "evidence" : "quiz");

  const load = async () => {
    const a = await api.get(`/phases/${phaseId}/assessment`);
    setData(a.data);
    const ev = await api.get(`/phases/${phaseId}/evidence/my`);
    setEvidence(ev.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [phaseId]);

  const submitQuiz = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/phases/${phaseId}/assessment/submit`, { answers });
      setResult(r);
      if (r.passed) toast.success(`Passed with ${r.score}%!`);
      else toast.error(`Scored ${r.score}%. Review the lessons and try again.`);
    } catch (e) { toast.error("Submit failed"); } finally { setBusy(false); }
  };

  const submitEvidence = async () => {
    if (!file) return toast.error("Please choose a screenshot to upload");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("note", note);
      const { data: r } = await api.post(`/phases/${phaseId}/evidence`, fd);
      setEvidence(r); setFile(null);
      toast.success("Evidence submitted! It's now under review.");
    } catch { toast.error("Upload failed"); } finally { setBusy(false); }
  };

  if (!data) return <Loader />;
  const wrongSet = result ? new Set(result.results.filter((r) => !r.is_correct).map((r) => r.question_id)) : null;

  return (
    <div className="max-w-2xl mx-auto">
      <Button variant="ghost" className="rounded-full mb-4 -ml-2" onClick={() => nav("/course")} data-testid="assessment-back"><ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Course</Button>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="quiz" data-testid="tab-quiz"><ClipboardCheck className="w-4 h-4 mr-1.5" /> Assessment</TabsTrigger>
          <TabsTrigger value="evidence" data-testid="tab-evidence"><FileCheck className="w-4 h-4 mr-1.5" /> Evidence</TabsTrigger>
        </TabsList>

        <TabsContent value="quiz">
          <div className="bg-white border border-border rounded-2xl shadow-sm p-6 sm:p-8">
            <h1 className="font-heading text-2xl font-bold">{data.assessment.title}</h1>
            <p className="text-muted-foreground text-sm mt-1">Passing score: {data.assessment.pass_score}%</p>

            {result && (
              <div className={`mt-5 p-4 rounded-xl flex items-center gap-3 ${result.passed ? "bg-brandgreen/10" : "bg-red-50"}`} data-testid="quiz-result">
                {result.passed ? <CheckCircle2 className="w-6 h-6 text-brandgreen" /> : <XCircle className="w-6 h-6 text-red-500" />}
                <div>
                  <p className="font-semibold">{result.passed ? "You passed!" : "Not quite yet"}</p>
                  <p className="text-sm text-muted-foreground">Your score: {result.score}%. {!result.passed && "Review the lessons and retry — wrong answers are marked but correct answers stay hidden."}</p>
                </div>
              </div>
            )}

            {data.questions.length === 0 ? (
              <p className="text-muted-foreground mt-6">No questions have been published yet.</p>
            ) : (
              <div className="mt-6 space-y-6">
                {data.questions.map((q, i) => {
                  const wrong = wrongSet?.has(q.id);
                  return (
                    <div key={q.id} className={`p-4 rounded-xl border ${wrong ? "border-red-200 bg-red-50/50" : "border-border"}`} data-testid={`question-${i}`}>
                      <p className="font-medium">{i + 1}. {q.question} {wrong && <span className="text-red-500 text-sm font-normal">(Your answer was incorrect)</span>}</p>
                      <RadioGroup className="mt-3 space-y-2" value={answers[q.id]?.toString()} disabled={!!result}
                        onValueChange={(v) => setAnswers((a) => ({ ...a, [q.id]: parseInt(v) }))}>
                        {q.options.map((opt, oi) => (
                          <div key={oi} className="flex items-center gap-2">
                            <RadioGroupItem value={oi.toString()} id={`${q.id}-${oi}`} data-testid={`opt-${i}-${oi}`} />
                            <Label htmlFor={`${q.id}-${oi}`} className="font-normal cursor-pointer">{opt}</Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>
                  );
                })}
                {result && !result.passed && (
                  <Button className="w-full rounded-full bg-teal hover:bg-teal/90" onClick={() => { setResult(null); setAnswers({}); }} data-testid="retry-quiz">Retry Assessment</Button>
                )}
                {!result && (
                  <Button className="w-full rounded-full bg-teal hover:bg-teal/90" onClick={submitQuiz} disabled={busy || Object.keys(answers).length < data.questions.length} data-testid="submit-quiz">
                    Submit Assessment
                  </Button>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="evidence">
          <div className="bg-white border border-border rounded-2xl shadow-sm p-6 sm:p-8">
            <h1 className="font-heading text-2xl font-bold">Submit Your Evidence</h1>
            <p className="text-muted-foreground text-sm mt-1">Upload a screenshot as proof of completing this phase. Our team will review it.</p>

            <div className="mt-4">
              <span className="text-sm mr-2">Current status:</span>
              <StatusBadge status={evidence?.status || "not_submitted"} />
            </div>

            {evidence?.status === "needs_revision" && evidence?.feedback && (
              <div className="mt-4 p-4 rounded-xl bg-red-50 border border-red-200" data-testid="evidence-feedback">
                <p className="text-sm font-medium text-red-700">Reviewer feedback:</p>
                <p className="text-sm text-red-600 mt-1">{evidence.feedback}</p>
              </div>
            )}
            {evidence?.status === "approved" && (
              <div className="mt-4 p-4 rounded-xl bg-brandgreen/10 flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-brandgreen" /><p className="text-sm">Approved! The next phase is unlocked.</p></div>
            )}

            {evidence?.status !== "approved" && (
              <div className="mt-5 space-y-4">
                <label className="block border-2 border-dashed border-border rounded-xl p-6 text-center cursor-pointer hover:border-teal transition-colors" data-testid="evidence-dropzone">
                  <Upload className="w-8 h-8 text-teal mx-auto mb-2" />
                  <span className="text-sm text-muted-foreground">{file ? file.name : "Click to choose a screenshot (PNG/JPG)"}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="evidence-file" />
                </label>
                <Textarea placeholder="Add a note for the reviewer (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="evidence-note" />
                <Button className="w-full rounded-full bg-brandgreen hover:bg-brandgreen/90" onClick={submitEvidence} disabled={busy} data-testid="submit-evidence-btn">
                  {evidence?.status === "under_review" || evidence?.status === "needs_revision" ? "Resubmit Evidence" : "Submit Evidence"}
                </Button>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
